import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { alpha } from "@mui/material/styles"; // for soft header tint
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { useMutation, useQuery } from "@apollo/client";
import {
  mapKind,
  mapPayerType,
} from "../../../../platform/careComponents/helper/referralHelpers";
import {
  CLAIM_CONCIERGE_BROADCAST_GROUP,
  CLAIM_CONCIERGE_CASE,
  CHANGE_REFERRAL_LEAD_STATUS,
  COMPLETE_CONCIERGE_INTAKE,
  ENABLE_PROVIDER_AGREEMENT,
  TRANSFER_CONCIERGE_BROADCAST_GROUP,
  TRANSFER_CONCIERGE_CASE,
  UPDATE_CONCIERGE_BROADCAST_GROUP_FOLLOW_UP,
  UNCLAIM_CONCIERGE_BROADCAST_GROUP,
  UNCLAIM_CONCIERGE_CASE,
} from "../../../../platform/graphql/mutations/care";
import {
  GET_CONCIERGE_BROADCAST_GROUPS,
  GET_CONCIERGE_LEADS,
} from "../../../../platform/graphql/queries/care";
import { GET_AGENT_USERS } from "../../../../platform/graphql/queries/user";
import ChatDialog from "../../../../platform/careComponents/components/chatDialog";
import { ReferrerContactPanel } from "../../../../platform/careComponents/components/referrerContactPanel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { rootClientPath } from "../../../../platform/util/link";
import {
  buildBookingStartShareMessage,
  buildBusinessRegisterShareMessage,
  buildCareJobsFindShareMessage,
  buildCareJobsPostShareMessage,
  buildCareReferShareMessage,
} from "../../../../platform/util/publicShareMessages";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import ConciergeCityBoardLeadRow from "./conciergeCityBoardLeadRow";
import {
  STAGE_LABELS,
  applyConciergeAgentState,
  formatCompensationLockedAt,
  formatInlineDateTime,
  getAgentRequestTone,
  getCompensationSourceLabel,
  getCompensationSummary,
  getFollowUpSummary,
  getLeadBroadcastGroupId,
  hasAgentFlag,
  mergeAgentOption,
  normalizeAgentOption,
  parseBroadcastSeed,
  parseExtra,
  shouldShowCompensationSourceChip,
  stringifyExtraIfNeeded,
  stringifySeedIfNeeded,
} from "./conciergeBoardShared";

/* ----------------------------------------------------------------------------- */
function normalizeRequestAreaLabel(value: any): string | null {
  const text = String(value || "").trim();
  if (!text) return null;
  return text
    .replace(/,\s*United States\b/i, "")
    .replace(/,\s*USA\b/i, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function getSubmittedRequestAreaLabel(rawExtra: any): string | null {
  const extra = parseExtra(rawExtra);
  const locationMeta = parseExtra(extra?.location);
  const careFindForm = parseExtra(extra?.careFindForm);
  return normalizeRequestAreaLabel(
    locationMeta?.submittedPreferredLocation ||
    locationMeta?.resolvedLabel ||
    locationMeta?.submittedCurrentLocation ||
    careFindForm?.preferredLocation ||
    careFindForm?.currentLocation ||
    locationMeta?.cityCode ||
    locationMeta?.cityName ||
    extra?.broadcastGeo?.cityName ||
    null,
  );
}

function getCityCode(l: any): string {
  return (
    getSubmittedRequestAreaLabel(l?.extra) ||
    normalizeRequestAreaLabel(l?.business?.cityName) ||
    "Unknown"
  );
}

function getBroadcastCityCode(group: any): string {
  const seed = parseBroadcastSeed(group?.seedJson);
  const requestAreaLabel = getSubmittedRequestAreaLabel(seed?.extra);
  if (requestAreaLabel) return requestAreaLabel;

  const firstBusinessCity =
    group?.invites?.find((inv: any) => inv?.business?.cityName)?.business?.cityName || null;
  if (firstBusinessCity) return normalizeRequestAreaLabel(firstBusinessCity) || firstBusinessCity;

  const extra = parseExtra(seed?.extra);
  return (
    normalizeRequestAreaLabel(
      extra?.location?.cityCode ||
      extra?.location?.cityName ||
      extra?.broadcastGeo?.cityName,
    ) ||
    "Unknown"
  );
}

function formatCityGroupLabel(city: string): string {
  return city === "Unknown" ? "Request Area: Unknown" : city;
}

function getBroadcastReferenceLabel(group: any): string | null {
  const seed = parseBroadcastSeed(group?.seedJson);
  const extra = parseExtra(seed?.extra);
  const value = String(
    seed?.residentKey || extra?.patientKey || extra?.patientInitials || ""
  ).trim();
  return value || null;
}

function getLeadReferenceLabel(lead: any): string | null {
  const extra = parseExtra(lead?.extra);
  const value = String(
    lead?.residentKey ||
    lead?.patientKey ||
    lead?.residentInitials ||
    extra?.patientKey ||
    extra?.patientInitials ||
    ""
  ).trim();
  return value || null;
}


function toDateTimeLocalInput(value: string | null | undefined) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  const hour = String(dt.getHours()).padStart(2, "0");
  const minute = String(dt.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function buildAgentAgreementUrl(rootClientPath: string, options: { leadId?: string | number | null; groupId?: string | number | null }) {
  const params = new URLSearchParams();
  if (options.leadId != null) params.set("lead", String(options.leadId));
  if (options.groupId != null) params.set("group", String(options.groupId));
  return `${rootClientPath}/care/agent/agreement?${params.toString()}`;
}

function buildTrackedCareFindLink(
  rootClientPath: string,
  options: { agentId?: string | number | null; agentName?: string | null },
) {
  const params = new URLSearchParams();
  const agentId = String(options.agentId || "").trim();
  const agentName = String(options.agentName || "").trim();

  if (agentId) params.set("agentId", agentId);
  if (agentName) params.set("agentName", agentName);
  if (agentId || agentName) params.set("sourceType", "AGENT_SOURCED");

  const query = params.toString();
  return `${rootClientPath}/referrals${query ? `?${query}` : ""}`;
}

function buildPublicClientUrl(rootClientPath: string, path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return new URL(normalizedPath, rootClientPath).toString();
}

function fallbackCopyText(text: string) {
  if (typeof document === "undefined") return;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

async function copyText(text: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  fallbackCopyText(text);
}

type WorkspaceShareLink = {
  label: string;
  message: string;
  path: string;
  url: string;
};

function WorkspaceShareLinksBox({ links }: { links: WorkspaceShareLink[] }) {
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    if (!feedback) return undefined;
    const timer = window.setTimeout(() => setFeedback(null), 2200);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const handleCopy = React.useCallback(async (link: WorkspaceShareLink) => {
    try {
      await copyText(link.url);
      setFeedback(`${link.label} copied`);
    } catch (_error) {
      setFeedback("Copy failed");
    }
  }, []);

  const handleShare = React.useCallback(async (link: WorkspaceShareLink) => {
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: link.label,
          text: link.message,
          url: link.url,
        });
        return;
      }
      await copyText(link.url);
      setFeedback(`${link.label} copied for sharing`);
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      try {
        await copyText(link.url);
        setFeedback(`${link.label} copied for sharing`);
      } catch (_copyError) {
        setFeedback("Share failed");
      }
    }
  }, []);

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
      sx={{
        borderRadius: 3,
        bgcolor: "rgba(17,47,72,0.04)",
        border: "1px solid rgba(17,47,72,0.08)",
        width: "100%",
        boxShadow: "none",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.4,
          py: 0.15,
          minHeight: 54,
          "& .MuiAccordionSummary-content": {
            my: 1,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", pr: 0.5 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Shared Links
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expand to copy or share your agent links.
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${links.length} links`}
            sx={{ borderRadius: 999, bgcolor: "#ffffff" }}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 1.4, pb: 1.2, pt: 0 }}>
        <Stack spacing={0.75}>
          <Box
            sx={{
              borderRadius: 2.5,
              overflow: "hidden",
              border: "1px solid rgba(17,47,72,0.08)",
              bgcolor: "#ffffff",
            }}
          >
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.path}>
                    <TableCell
                      sx={{
                        py: 0.6,
                        pr: 1,
                        borderBottomColor: "rgba(17,47,72,0.08)",
                      }}
                    >
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ color: "#16354c", fontWeight: 700 }}>
                          {link.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            display: "block",
                            color: "#557286",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {link.path}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        py: 0.35,
                        pl: 0,
                        width: 78,
                        borderBottomColor: "rgba(17,47,72,0.08)",
                      }}
                    >
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="Copy link">
                          <IconButton
                            size="small"
                            aria-label={`Copy ${link.path}`}
                            onClick={() => void handleCopy(link)}
                            sx={{ width: 30, height: 30 }}
                          >
                            <ContentCopyRoundedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share link">
                          <IconButton
                            size="small"
                            aria-label={`Share ${link.path}`}
                            onClick={() => void handleShare(link)}
                            sx={{ width: 30, height: 30 }}
                          >
                            <ShareRoundedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {feedback ? (
            <Typography variant="caption" color="text.secondary">
              {feedback}
            </Typography>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function getRequestAgeMeta(value: any): {
  label: string;
  color: "default" | "warning" | "error";
  days: number | null;
} {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) {
    return { label: "Age unknown", color: "default", days: null };
  }
  const now = new Date();
  const start = new Date(d);
  start.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.max(
    0,
    Math.floor((today.getTime() - start.getTime()) / 86400000),
  );
  if (diffDays >= 3) {
    return { label: `${diffDays} days waiting`, color: "error", days: diffDays };
  }
  if (diffDays >= 1) {
    return {
      label: `${diffDays} day${diffDays === 1 ? "" : "s"} waiting`,
      color: "warning",
      days: diffDays,
    };
  }
  return { label: "New today", color: "default", days: 0 };
}

function formatDateWithAgeLabel(value: any) {
  const age = getRequestAgeMeta(value);
  const ageLabel = age.days !== null ? (age.days === 0 ? "0d" : `${age.days}d`) : "";
  return `${formatInlineDateTime(value)}${ageLabel ? ` • ${ageLabel}` : ""}`;
}

function getDetailText(value: any): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(value || "").trim();
}

function ScreeningRequestRow({
  group,
  rootClientPath,
  currentAgentId,
  canActAsAgent = true,
  onOpenChat,
  onClaim,
  onUnclaim,
  onTransfer,
  claiming,
  unclaiming,
  transferring,
  agentOptions = [],
}: {
  group: any;
  rootClientPath: string;
  currentAgentId?: string | number | null;
  canActAsAgent?: boolean;
  onOpenChat?: (v: { id: string; name?: string | null }) => void;
  onClaim: () => void | Promise<void>;
  onUnclaim: () => void | Promise<void>;
  onTransfer?: (targetAgentId: string, note: string) => any;
  claiming?: boolean;
  unclaiming?: boolean;
  transferring?: boolean;
  agentOptions?: Array<{ id: string; name: string; email?: string | null }>;
}) {
  const [updateGroupFollowUp, { loading: updatingGroupFollowUp }] = useMutation(
    UPDATE_CONCIERGE_BROADCAST_GROUP_FOLLOW_UP,
  );
  const [transferOpen, setTransferOpen] = React.useState(false);
  const [transferTargetId, setTransferTargetId] = React.useState("");
  const [transferNote, setTransferNote] = React.useState("");
  const [followUpDialogOpen, setFollowUpDialogOpen] = React.useState(false);
  const [followUpOverride, setFollowUpOverride] = React.useState<any | null>(null);
  const [detailsExpanded, setDetailsExpanded] = React.useState(false);
  const seed = parseBroadcastSeed(group?.seedJson);
  const displayGroup = followUpOverride || group;
  const displaySeed = parseBroadcastSeed(displayGroup?.seedJson);
  const extra = parseExtra(displaySeed?.extra);
  const concierge = extra?.concierge || {};
  const conciergeStatus: string = concierge.status || "REQUESTED";
  const ownerAgentId = concierge.ownerAgentId ?? group?.ownerAgentId ?? group?.assignedAgentId ?? null;
  const ownedByMe =
    currentAgentId != null &&
    ownerAgentId != null &&
    String(ownerAgentId) === String(currentAgentId);
  const canClaim = canActAsAgent && !ownerAgentId;
  const canUnclaim = canActAsAgent && ownedByMe;
  const claimedByAnother = !!ownerAgentId && !ownedByMe;
  const transferChoices = agentOptions.filter(
    (option) => String(option.id || "") && String(option.id) !== String(ownerAgentId || ""),
  );
  const assignedAgent = React.useMemo(
    () =>
      agentOptions.find(
        (option) => String(option.id || "") === String(ownerAgentId || ""),
      ) || null,
    [agentOptions, ownerAgentId],
  );
  const assignedAgentLabel =
    assignedAgent?.name ||
    String(group?.ownerAgentName || "").trim() ||
    String(group?.assignedAgentName || "").trim() ||
    String(concierge?.ownerAgentName || "").trim() ||
    (ownerAgentId ? `Agent ${ownerAgentId}` : "");
  const residentLabel =
    seed?.residentKey || extra?.patientKey || extra?.patientInitials || `Group ${group?.id}`;
  const payerLabel = mapPayerType(seed?.payerType || extra?.payerType || "PRIVATE");
  const kindLabel = mapKind(seed?.kind || "referral");
  const leadSourceType = String(
    group?.leadSourceType || seed?.leadSourceType || extra?.leadSource?.type || "PLATFORM_DIRECT"
  )
    .trim()
    .toUpperCase();
  const sourceAgentName =
    String(group?.sourceAgentName || extra?.leadSource?.sourceAgentName || "").trim() || null;
  const notes = seed?.intakeNotes || extra?.notes || "";
  const careFindForm = parseExtra(extra?.careFindForm);
  const submissionMeta = parseExtra(extra?.submissionMeta);
  const locationMeta = parseExtra(extra?.location);
  const currentLocationLabel =
    getDetailText(locationMeta?.submittedCurrentLocation) ||
    getDetailText(careFindForm?.currentLocation);
  const preferredLocationLabel =
    getDetailText(locationMeta?.submittedPreferredLocation) ||
    getDetailText(careFindForm?.preferredLocation);
  const careFindDetails = React.useMemo(
    () =>
      [
        { label: "Resident age", value: getDetailText(careFindForm?.residentAge) },
        { label: "Gender", value: getDetailText(careFindForm?.gender) },
        { label: "Current location", value: currentLocationLabel },
        { label: "Current situation", value: getDetailText(careFindForm?.currentSituation) },
        { label: "Care needs", value: getDetailText(careFindForm?.careNeeds) },
        { label: "Payment type", value: payerLabel && payerLabel !== "Unknown" ? payerLabel : "" },
        { label: "Budget", value: getDetailText(careFindForm?.budget) },
        { label: "Timeline", value: getDetailText(careFindForm?.timeline) },
        { label: "Preferred location", value: preferredLocationLabel },
        { label: "Private room needed", value: getDetailText(careFindForm?.privateRoomNeeded) },
        {
          label: "Caregiver preference",
          value: getDetailText(careFindForm?.caregiverPreference),
        },
        {
          label: "Additional details",
          value: getDetailText(careFindForm?.notes || notes),
          fullWidth: true,
        },
      ].filter((item) => item.value),
    [careFindForm, currentLocationLabel, notes, payerLabel, preferredLocationLabel],
  );
  const showCareFindDetails =
    careFindDetails.length > 0 ||
    ["care-find", "care-refer"].includes(getDetailText(submissionMeta?.submittedVia));
  const compensationSummary = React.useMemo(
    () => getCompensationSummary(displayGroup, extra),
    [displayGroup, extra],
  );
  const followUpSummary = React.useMemo(
    () => getFollowUpSummary(displayGroup, extra),
    [displayGroup, extra],
  );
  const referrerContact = extra?.referrerContact || {};
  const referrerName = referrerContact?.name || "";
  const referrerEmail = referrerContact?.email || "";
  const referrerPhone = referrerContact?.phone || "";
  const groupChatId = group?.id ? `broadcast-group:${group.id}` : "";
  const invites: any[] = Array.isArray(group?.invites) ? group.invites : [];
  const normalizedInviteStatuses = invites.map((inv) => String(inv?.status || "").toUpperCase());
  const draftCount = normalizedInviteStatuses.filter((status) => status === "DRAFT").length;
  const cardTone = getAgentRequestTone(conciergeStatus);
  const screeningSummaryText =
    draftCount > 0
      ? "Private-pay request is waiting on the assigned agent to send it to providers."
      : "Private-pay request awaiting concierge screening before provider agreement steps.";
  const handleTransferSubmit = async () => {
    if (!transferTargetId || !onTransfer) return;
    const resp = await onTransfer(transferTargetId, transferNote);
    if (resp?.data?.transferConciergeBroadcastGroup?.success === false) return;
    setTransferOpen(false);
    setTransferTargetId("");
    setTransferNote("");
  };

  const [followUpForm, setFollowUpForm] = React.useState({
    followUpStatus: followUpSummary.followUpStatus || "PENDING_ASSIGNMENT",
    nextFollowUpAt: toDateTimeLocalInput(followUpSummary.nextFollowUpAt),
    lastFollowUpAt: toDateTimeLocalInput(followUpSummary.lastFollowUpAt),
    followUpNotes: followUpSummary.followUpNotes || "",
  });

  React.useEffect(() => {
    setFollowUpForm({
      followUpStatus: followUpSummary.followUpStatus || "PENDING_ASSIGNMENT",
      nextFollowUpAt: toDateTimeLocalInput(followUpSummary.nextFollowUpAt),
      lastFollowUpAt: toDateTimeLocalInput(followUpSummary.lastFollowUpAt),
      followUpNotes: followUpSummary.followUpNotes || "",
    });
  }, [
    followUpSummary.followUpNotes,
    followUpSummary.followUpStatus,
    followUpSummary.lastFollowUpAt,
    followUpSummary.nextFollowUpAt,
  ]);

  const handleFollowUpSubmit = async () => {
    const payload = {
      followUpStatus: followUpForm.followUpStatus,
      nextFollowUpAt: followUpForm.nextFollowUpAt
        ? new Date(followUpForm.nextFollowUpAt).toISOString()
        : null,
      lastFollowUpAt: followUpForm.lastFollowUpAt
        ? new Date(followUpForm.lastFollowUpAt).toISOString()
        : null,
      followUpNotes: followUpForm.followUpNotes.trim() || null,
    };
    const resp = await updateGroupFollowUp({
      variables: {
        groupId: group.id,
        input: payload,
      },
    });
    const updated = resp?.data?.updateConciergeBroadcastGroupFollowUp;
    if (updated) {
      const nextSeed = {
        ...displaySeed,
        extra: {
          ...(parseExtra(displaySeed?.extra)),
          followUp: {
            ...(parseExtra(displaySeed?.extra)?.followUp || {}),
            status: updated.followUpStatus,
            lastFollowUpAt: updated.lastFollowUpAt,
            nextFollowUpAt: updated.nextFollowUpAt,
            notes: updated.followUpNotes,
          },
        },
      };
      setFollowUpOverride({
        ...group,
        ...updated,
        seedJson: stringifySeedIfNeeded(group?.seedJson, nextSeed),
      });
    }
    setFollowUpDialogOpen(false);
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: cardTone.bodyBg,
        boxShadow: "0 8px 24px rgba(15, 31, 46, 0.05)",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.25, sm: 1.5 },
          py: 1,
          bgcolor: cardTone.headerBg,
          borderBottom: "1px solid rgba(103, 114, 126, 0.08)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1.25, md: 2 }}
          alignItems={{ xs: "flex-start", md: "flex-start" }}
          sx={{ width: "100%" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {residentLabel}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 0.9 }}
            >
              <Chip size="small" color="warning" label="Private Screening" />
              <Chip
                size="small"
                label={`Concierge - ${conciergeStatus}${ownedByMe ? " (me)" : ownerAgentId ? "" : " (unassigned)"
                  }`}
                color={conciergeStatus === "ASSIGNED" ? "info" : "warning"}
                variant="outlined"
              />
              {assignedAgentLabel ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Assigned: ${ownedByMe ? `${assignedAgentLabel} (me)` : assignedAgentLabel}`}
                />
              ) : null}
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1 }}
            >
              <Chip size="small" variant="outlined" label={kindLabel} />
              {leadSourceType === "AGENT_SOURCED" ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={sourceAgentName ? `Source: ${sourceAgentName}` : "Agent-sourced"}
                />
              ) : null}
              {showCareFindDetails ? (
                <Chip size="small" variant="outlined" label="Referral Form" />
              ) : null}
              {payerLabel && payerLabel !== "Unknown" ? (
                <Chip size="small" variant="outlined" label={payerLabel} />
              ) : null}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ md: "flex-end" }}>
            {canClaim ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<VolunteerActivismIcon />}
                onClick={onClaim}
                disabled={claiming}
              >
                Claim
              </Button>
            ) : null}
            {claimedByAnother ? (
              <Tooltip title="This screening request is already assigned to another concierge.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VolunteerActivismIcon />}
                    disabled
                  >
                    Claimed
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {canUnclaim ? (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={onUnclaim}
                disabled={unclaiming}
              >
                Unclaim
              </Button>
            ) : null}
            {canActAsAgent && ownedByMe && transferChoices.length > 0 ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setTransferOpen(true)}
              >
                Transfer
              </Button>
            ) : null}
            {!canActAsAgent ? (
              <Tooltip title="Agent actions are disabled for admin-only accounts.">
                <span>
                  <Button size="small" variant="outlined" disabled>
                    View Only
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            <Button
              size="small"
              variant="outlined"
              href={`${rootClientPath}/care/referrals/group/${group.id}`}
            >
              Open Broadcast Group
            </Button>
            <Button
              size="small"
              variant="outlined"
              href={buildAgentAgreementUrl(rootClientPath, { groupId: group.id })}
            >
              View Agreement
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Accordion
        disableGutters
        expanded={detailsExpanded}
        onChange={(_event, nextExpanded) => setDetailsExpanded(nextExpanded)}
        sx={{
          bgcolor: "transparent",
          boxShadow: "none",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: { xs: 1.25, sm: 1.5 },
            py: 0.6,
            minHeight: 60,
            "& .MuiAccordionSummary-content": {
              my: 0.8,
            },
          }}
        >
          <Stack spacing={0.45} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Private Request Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {screeningSummaryText}
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ px: { xs: 1.25, sm: 1.5 }, pb: { xs: 1.5, sm: 1.75 }, pt: 0 }}>
          <Stack spacing={1.25}>
            {claimedByAnother ? (
              <Typography variant="body2" color="warning.main">
                Assigned to another concierge.
              </Typography>
            ) : null}

            {notes && !showCareFindDetails ? (
              <Typography variant="body2">{notes}</Typography>
            ) : null}

            {showCareFindDetails ? (
              <Box
                sx={{
                  px: 1.25,
                  py: 1.15,
                  borderRadius: 1.75,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                  border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.16)}`,
                }}
              >
                <Stack spacing={1}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.35 }}
                    >
                      Request Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Information already provided by the customer.
                    </Typography>
                  </Box>
                  {careFindDetails.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                        gap: 1.25,
                      }}
                    >
                      {careFindDetails.map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            minWidth: 0,
                            gridColumn: item.fullWidth ? { md: "1 / -1" } : undefined,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.15, whiteSpace: item.fullWidth ? "pre-wrap" : "normal" }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      This request came from the shared referral form.
                    </Typography>
                  )}
                </Stack>
              </Box>
            ) : null}

            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ReferrerContactPanel
                  referrer={{
                    name: referrerName || null,
                    email: referrerEmail || null,
                    phone: referrerPhone || null,
                  }}
                  title="Referrer Contact"
                  defaultExpanded={true}
                  leadStatus="PLACED"
                >
                  {groupChatId ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canActAsAgent}
                        onClick={() =>
                          onOpenChat?.({
                            id: groupChatId,
                            name: referrerName || null,
                          })
                        }
                      >
                        Chat with Referrer
                      </Button>
                    </Stack>
                  ) : null}
                </ReferrerContactPanel>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.75,
                  bgcolor: (t) => alpha(t.palette.info.main, 0.04),
                  border: (t) => `1px solid ${alpha(t.palette.info.main, 0.18)}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                  Agent Compensation Agreement
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" sx={{ mb: 0.75 }}>
                  <Chip size="small" variant="outlined" label={compensationSummary.agreementLabel} />
                  <Chip
                    size="small"
                    color={compensationSummary.compensationPercent != null ? "info" : "default"}
                    variant="outlined"
                    label={
                      compensationSummary.compensationPercent != null
                        ? `${compensationSummary.compensationPercent}% of Plany success fee`
                        : "Compensation pending"
                    }
                  />
                  {shouldShowCompensationSourceChip(
                    compensationSummary.agreementLabel,
                    compensationSummary.compensationSourceType,
                  ) ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={getCompensationSourceLabel(compensationSummary.compensationSourceType)}
                    />
                  ) : null}
                  {compensationSummary.recipientAgentName ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Paid to: ${compensationSummary.recipientAgentName}`}
                    />
                  ) : null}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {compensationSummary.compensationNotes ||
                    "Plany locks compensation terms once an agent owns the request so agent-sourced and Plany-assigned cases follow different payout rules."}
                </Typography>
                {compensationSummary.compensationLockedAt || compensationSummary.agentAgreementVersion ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {compensationSummary.compensationLockedAt
                      ? `Locked ${formatCompensationLockedAt(compensationSummary.compensationLockedAt)}`
                      : "Not locked yet"}
                    {compensationSummary.agentAgreementVersion
                      ? ` • ${compensationSummary.agentAgreementVersion}`
                      : ""}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Dialog open={followUpDialogOpen} onClose={() => setFollowUpDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update Screening Follow-up</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Follow-up status"
              value={followUpForm.followUpStatus}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, followUpStatus: event.target.value }))
              }
              fullWidth
            >
              <MenuItem value="PENDING_ASSIGNMENT">Pending assignment</MenuItem>
              <MenuItem value="NEEDS_OUTREACH">Needs outreach</MenuItem>
              <MenuItem value="WAITING_ON_REFERRER">Waiting on referrer</MenuItem>
              <MenuItem value="WAITING_ON_PROVIDER">Waiting on provider</MenuItem>
              <MenuItem value="FOLLOW_UP_SCHEDULED">Follow-up scheduled</MenuItem>
              <MenuItem value="COMPLETE">Complete</MenuItem>
            </TextField>
            <TextField
              label="Next follow-up"
              type="datetime-local"
              value={followUpForm.nextFollowUpAt}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, nextFollowUpAt: event.target.value }))
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Last follow-up"
              type="datetime-local"
              value={followUpForm.lastFollowUpAt}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, lastFollowUpAt: event.target.value }))
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Follow-up notes"
              value={followUpForm.followUpNotes}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, followUpNotes: event.target.value }))
              }
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowUpDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleFollowUpSubmit} disabled={updatingGroupFollowUp}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Transfer Screening Request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Target agent"
              value={transferTargetId}
              onChange={(event) => setTransferTargetId(event.target.value)}
              fullWidth
            >
              {transferChoices.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                  {option.email ? ` (${option.email})` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Transfer note"
              value={transferNote}
              onChange={(event) => setTransferNote(event.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransferSubmit}
            disabled={!transferTargetId || transferring}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ----------------------------------------------------------------------------- */
/* Lead row: agent actions plus provider invite on Enable Agreement */
export default function ConciergeCityBoardPage({
  cityFilter = [],
}: {
  cityFilter?: string[];
}) {
  const isProd = process.env.NODE_ENV === "production";
  const [isPageVisible, setIsPageVisible] = React.useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );
  const livePollMs = isPageVisible ? (isProd ? 30000 : 15000) : 0;
  const userContext = React.useContext(UserContext);
  const providerUser = userContext.providerUser;
  const currentAgentId = providerUser?.userId;
  const providerUserIsAgent = hasAgentFlag(providerUser);

  // Dialog states
  const [chatProvider, setChatProvider] = React.useState<null | {
    id: string;
    name?: string | null;
  }>(null);
  const [chatReferrer, setChatReferrer] = React.useState<null | {
    id: string;
    name?: string | null;
  }>(null);
  const [tourEnabledByLeadId, setTourEnabledByLeadId] = React.useState<
    Record<string, boolean>
  >({});
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [expandedAcceptedGroups, setExpandedAcceptedGroups] = React.useState<
    Record<string, boolean>
  >({});
  const [expandedRequestGroups, setExpandedRequestGroups] = React.useState<
    Record<string, boolean>
  >({});
  const [expandedAcceptedLeads, setExpandedAcceptedLeads] = React.useState<
    Record<string, boolean>
  >({});
  const [workspaceTab, setWorkspaceTab] = React.useState(0);
  const trackedCareFindLink = React.useMemo(
    () =>
      buildTrackedCareFindLink(rootClientPath, {
        agentId: providerUserIsAgent ? currentAgentId : null,
        agentName: providerUserIsAgent
          ? String(providerUser?.username || providerUser?.email || "").trim() || null
          : null,
      }),
    [currentAgentId, providerUser?.email, providerUser?.username, providerUserIsAgent],
  );
  const shareableCareFindLink = React.useMemo(() => {
    if (typeof window === "undefined") return trackedCareFindLink;
    return new URL(trackedCareFindLink, window.location.origin).toString();
  }, [trackedCareFindLink]);
  const workspaceShareLinks = React.useMemo(
    () => {
      const businessRegisterLink = buildPublicClientUrl(rootClientPath, "/business/register");
      const bookingStartLink = buildPublicClientUrl(rootClientPath, "/booking/start");
      const careJobsFindLink = buildPublicClientUrl(rootClientPath, "/care/jobs/find");
      const careJobsPostLink = buildPublicClientUrl(rootClientPath, "/care/jobs/post");

      return [
        {
          label: "Referrer landing page",
          path: "/referrals",
          url: shareableCareFindLink,
          message: buildCareReferShareMessage(shareableCareFindLink),
        },
        {
          label: "Business registration",
          path: "/business/register",
          url: businessRegisterLink,
          message: buildBusinessRegisterShareMessage(businessRegisterLink),
        },
        {
          label: "Booking start",
          path: "/booking/start",
          url: bookingStartLink,
          message: buildBookingStartShareMessage(bookingStartLink),
        },
        {
          label: "Care job search",
          path: "/care/jobs/find",
          url: careJobsFindLink,
          message: buildCareJobsFindShareMessage(careJobsFindLink),
        },
        {
          label: "Care job posting",
          path: "/care/jobs/post",
          url: careJobsPostLink,
          message: buildCareJobsPostShareMessage(careJobsPostLink),
        },
      ];
    },
    [shareableCareFindLink],
  );

  const { data, loading, error, refetch } = useQuery(GET_CONCIERGE_LEADS, {
    variables: {
      cityCodes: cityFilter?.length ? cityFilter : null,
      statuses: null,
      limit: 300,
      offset: 0,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: livePollMs,
  });
  const {
    data: screeningData,
    loading: screeningLoading,
    error: screeningError,
    refetch: refetchScreeningGroups,
  } = useQuery(GET_CONCIERGE_BROADCAST_GROUPS, {
    variables: {
      limit: 300,
      offset: 0,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: livePollMs,
  });
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  const { data: agentUsersData } = useQuery(GET_AGENT_USERS, {
    skip: !providerUser?.userId,
    fetchPolicy: "cache-and-network",
  });
  const leads = React.useMemo(
    () => data?.getConciergeLeads ?? [],
    [data],
  );
  const screeningGroups = React.useMemo(
    () => {
      const rows = (screeningData?.getConciergeBroadcastGroups ?? []) as any[];
      const byId = new Map<string, any>();
      rows.forEach((group) => {
        const id = String(group?.id || "").trim();
        if (!id) return;
        if (!byId.has(id)) {
          byId.set(id, group);
          return;
        }

        const existing = byId.get(id);
        const existingInviteCount = Array.isArray(existing?.invites) ? existing.invites.length : 0;
        const nextInviteCount = Array.isArray(group?.invites) ? group.invites.length : 0;
        if (nextInviteCount >= existingInviteCount) {
          byId.set(id, group);
        }
      });
      return Array.from(byId.values());
    },
    [screeningData],
  );
  const agentOptions = React.useMemo(() => {
    const map = new Map<string, { id: string; name: string; email?: string | null }>();
    const add = (input: any) => {
      const normalized = normalizeAgentOption(input);
      if (!normalized) return;
      map.set(normalized.id, mergeAgentOption(map.get(normalized.id), normalized));
    };
    if (providerUserIsAgent) {
      add({
        id: providerUser?.userId,
        name: providerUser?.username,
        email: providerUser?.email,
      });
    }

    (agentUsersData?.agentUsers || []).forEach((user: any) => {
      if (!hasAgentFlag(user)) return;
      add({
        id: user?.id,
        name: user?.username || user?.email,
        email: user?.email,
      });
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [
    agentUsersData?.agentUsers,
    providerUser?.email,
    providerUser?.userId,
    providerUser?.username,
    providerUserIsAgent,
  ]);

  const hasWorkspaceAccess = React.useMemo(() => {
    if (providerUserIsAgent) return true;
    if (providerUser?.isPlatformAdmin === true) return true;
    const currentUserId = String(providerUser?.userId || "").trim();
    if (!currentUserId) return false;
    return (agentUsersData?.agentUsers || []).some(
      (user: any) => String(user?.id || "").trim() === currentUserId,
    );
  }, [
    agentUsersData?.agentUsers,
    providerUser?.isPlatformAdmin,
    providerUser?.userId,
    providerUserIsAgent,
  ]);

  const resolveAgentOptionById = React.useCallback(
    (agentId: any) =>
      agentOptions.find((option) => String(option.id || "") === String(agentId || "")) || null,
    [agentOptions],
  );

  React.useEffect(() => {
    const handleWindowRefresh = () => {
      refetch();
      refetchScreeningGroups();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
        refetchScreeningGroups();
      }
    };

    window.addEventListener("focus", handleWindowRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refetch, refetchScreeningGroups]);

  const [claimCase, { loading: claiming }] = useMutation(CLAIM_CONCIERGE_CASE, {
    optimisticResponse: (_vars) => ({
      claimConciergeCase: {
        __typename: "ClaimConciergeCaseResult",
        success: true,
        message: "Case claimed.",
        concierge: {
          __typename: "ConciergeState",
          status: "ASSIGNED",
          ownerAgentId: String(currentAgentId ?? ""),
          ownerAgentName: String(providerUser?.username || providerUser?.email || ""),
          slaAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        },
      },
    }),
    update: (cache, { data: resp }, { variables }) => {
      if (!variables?.leadId) return;
      const leadId = String(variables.leadId);
      const prev = cache.readQuery<any>({
        query: GET_CONCIERGE_LEADS,
        variables: {
          cityCodes: cityFilter?.length ? cityFilter : null,
          statuses: null,
          limit: 300,
          offset: 0,
        },
      });
      if (!prev?.getConciergeLeads) return;

      const next = {
        ...prev,
        getConciergeLeads: prev.getConciergeLeads.map((row: any) => {
          if (String(row.id) !== leadId) return row;
          const ex = parseExtra(row.extra);
          ex.concierge = applyConciergeAgentState(ex.concierge, {
            status: "ASSIGNED",
            ownerAgentId: String(currentAgentId ?? ""),
            ownerAgentName:
              resp?.claimConciergeCase?.concierge?.ownerAgentName ||
              String(providerUser?.username || providerUser?.email || ""),
            agent: {
              id: String(currentAgentId ?? ""),
              name: String(providerUser?.username || providerUser?.email || ""),
              email: providerUser?.email || null,
            },
            slaAt:
              resp?.claimConciergeCase?.concierge?.slaAt ||
              new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          });
          return { ...row, extra: stringifyExtraIfNeeded(row.extra, ex) };
        }),
      };
      cache.writeQuery({
        query: GET_CONCIERGE_LEADS,
        variables: {
          cityCodes: cityFilter?.length ? cityFilter : null,
          statuses: null,
          limit: 300,
          offset: 0,
        },
        data: next,
      });
    },
    onCompleted: () => refetch(),
  });
  const [claimBroadcastGroup, { loading: claimingBroadcastGroup }] = useMutation(
    CLAIM_CONCIERGE_BROADCAST_GROUP,
    {
      optimisticResponse: ({ groupId }: any) => ({
        claimConciergeBroadcastGroup: {
          __typename: "ClaimConciergeCaseResult",
          success: true,
          message: "Screening request claimed.",
          concierge: {
            __typename: "ConciergeState",
            status: "ASSIGNED",
            ownerAgentId: String(currentAgentId ?? ""),
            ownerAgentName: String(providerUser?.username || providerUser?.email || ""),
            slaAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          },
        },
      }),
      update: (cache, { data: resp }, { variables }) => {
        if (!variables?.groupId) return;
        const groupId = String(variables.groupId);
        const prev = cache.readQuery<any>({
          query: GET_CONCIERGE_BROADCAST_GROUPS,
          variables: {
            limit: 300,
            offset: 0,
          },
        });
        if (!prev?.getConciergeBroadcastGroups) return;

        const next = {
          ...prev,
          getConciergeBroadcastGroups: prev.getConciergeBroadcastGroups.map(
            (group: any) => {
              if (String(group.id) !== groupId) return group;
              const seed = parseBroadcastSeed(group.seedJson);
              const extra = parseExtra(seed?.extra);
              extra.concierge = applyConciergeAgentState(extra.concierge, {
                status: "ASSIGNED",
                ownerAgentId: String(currentAgentId ?? ""),
                ownerAgentName:
                  resp?.claimConciergeBroadcastGroup?.concierge?.ownerAgentName ||
                  String(providerUser?.username || providerUser?.email || ""),
                agent: {
                  id: String(currentAgentId ?? ""),
                  name: String(providerUser?.username || providerUser?.email || ""),
                  email: providerUser?.email || null,
                },
                slaAt:
                  resp?.claimConciergeBroadcastGroup?.concierge?.slaAt ||
                  new Date(Date.now() + 30 * 60 * 1000).toISOString(),
              });
              extra.referrerStatusMessage =
                "Our team is reviewing your request and will get back to you shortly.";
              seed.extra = extra;
              return {
                ...group,
                seedJson: stringifySeedIfNeeded(group.seedJson, seed),
              };
            },
          ),
        };
        cache.writeQuery({
          query: GET_CONCIERGE_BROADCAST_GROUPS,
          variables: {
            limit: 300,
            offset: 0,
          },
          data: next,
        });
      },
      onError: (err) => {
        setActionError(err.message || "Could not claim screening request.");
      },
      onCompleted: async () => {
        await Promise.all([refetch(), refetchScreeningGroups()]);
      },
    },
  );

  const [unclaimCase, { loading: unclaiming }] = useMutation(
    UNCLAIM_CONCIERGE_CASE,
    {
      optimisticResponse: (_vars) => ({
        unclaimConciergeCase: {
          __typename: "ClaimConciergeCaseResult",
          success: true,
          message: "Case unclaimed.",
          concierge: {
            __typename: "ConciergeState",
            status: "REQUESTED",
            ownerAgentId: null,
            ownerAgentName: null,
            slaAt: null,
          },
        },
      }),
      update: (cache, _resp, { variables }) => {
        if (!variables?.leadId) return;
        const leadId = String(variables.leadId);
        const prev = cache.readQuery<any>({
          query: GET_CONCIERGE_LEADS,
          variables: {
            cityCodes: cityFilter?.length ? cityFilter : null,
            statuses: null,
            limit: 300,
            offset: 0,
          },
        });
        if (!prev?.getConciergeLeads) return;

        const next = {
          ...prev,
          getConciergeLeads: prev.getConciergeLeads.map((row: any) => {
            if (String(row.id) !== leadId) return row;
            const ex = parseExtra(row.extra);
            ex.concierge = applyConciergeAgentState(ex.concierge, {
              status: "REQUESTED",
              ownerAgentId: null,
              ownerAgentName: null,
              agent: null,
              slaAt: null,
            });
            return { ...row, extra: stringifyExtraIfNeeded(row.extra, ex) };
          }),
        };
        cache.writeQuery({
          query: GET_CONCIERGE_LEADS,
          variables: {
            cityCodes: cityFilter?.length ? cityFilter : null,
            statuses: null,
            limit: 300,
            offset: 0,
          },
          data: next,
        });
      },
      onCompleted: () => refetch(),
    }
  );
  const [unclaimBroadcastGroup, { loading: unclaimingBroadcastGroup }] = useMutation(
    UNCLAIM_CONCIERGE_BROADCAST_GROUP,
    {
      optimisticResponse: ({ groupId }: any) => ({
        unclaimConciergeBroadcastGroup: {
          __typename: "ClaimConciergeCaseResult",
          success: true,
          message: "Screening request unclaimed.",
          concierge: {
            __typename: "ConciergeState",
            status: "REQUESTED",
            ownerAgentId: null,
            ownerAgentName: null,
            slaAt: null,
          },
        },
      }),
      update: (cache, _resp, { variables }) => {
        if (!variables?.groupId) return;
        const groupId = String(variables.groupId);
        const prev = cache.readQuery<any>({
          query: GET_CONCIERGE_BROADCAST_GROUPS,
          variables: {
            limit: 300,
            offset: 0,
          },
        });
        if (!prev?.getConciergeBroadcastGroups) return;

        const next = {
          ...prev,
          getConciergeBroadcastGroups: prev.getConciergeBroadcastGroups.map(
            (group: any) => {
              if (String(group.id) !== groupId) return group;
              const seed = parseBroadcastSeed(group.seedJson);
              const extra = parseExtra(seed?.extra);
              extra.concierge = applyConciergeAgentState(extra.concierge, {
                status: "REQUESTED",
                ownerAgentId: null,
                ownerAgentName: null,
                agent: null,
                slaAt: null,
              });
              delete extra.referrerStatusMessage;
              seed.extra = extra;
              return {
                ...group,
                seedJson: stringifySeedIfNeeded(group.seedJson, seed),
              };
            },
          ),
        };
        cache.writeQuery({
          query: GET_CONCIERGE_BROADCAST_GROUPS,
          variables: {
            limit: 300,
            offset: 0,
          },
          data: next,
        });
      },
      onError: (err) => {
        setActionError(err.message || "Could not unclaim screening request.");
      },
      onCompleted: async () => {
        await Promise.all([refetch(), refetchScreeningGroups()]);
      },
    },
  );
  const [transferCase, { loading: transferringCase }] = useMutation(
    TRANSFER_CONCIERGE_CASE,
    {
      optimisticResponse: ({ targetAgentId }: any) => {
        const target = resolveAgentOptionById(targetAgentId);
        return {
          transferConciergeCase: {
            __typename: "ClaimConciergeCaseResult",
            success: true,
            message: "Case transferred.",
            concierge: {
              __typename: "ConciergeState",
              status: "ASSIGNED",
              ownerAgentId: String(targetAgentId || ""),
              ownerAgentName: target?.name || null,
              slaAt: null,
            },
          },
        };
      },
      update: (cache, { data: resp }, { variables }) => {
        if (!variables?.leadId) return;
        const leadId = String(variables.leadId);
        const target = resolveAgentOptionById(variables?.targetAgentId);
        const prev = cache.readQuery<any>({
          query: GET_CONCIERGE_LEADS,
          variables: {
            cityCodes: cityFilter?.length ? cityFilter : null,
            statuses: null,
            limit: 300,
            offset: 0,
          },
        });
        if (!prev?.getConciergeLeads) return;
        cache.writeQuery({
          query: GET_CONCIERGE_LEADS,
          variables: {
            cityCodes: cityFilter?.length ? cityFilter : null,
            statuses: null,
            limit: 300,
            offset: 0,
          },
          data: {
            ...prev,
            getConciergeLeads: prev.getConciergeLeads.map((row: any) => {
              if (String(row.id) !== leadId) return row;
              const ex = parseExtra(row.extra);
              ex.concierge = applyConciergeAgentState(ex.concierge, {
                status: "ASSIGNED",
                ownerAgentId: String(variables?.targetAgentId || ""),
                ownerAgentName:
                  resp?.transferConciergeCase?.concierge?.ownerAgentName ||
                  target?.name ||
                  null,
                agent: target
                  ? { id: String(target.id), name: target.name, email: target.email || null }
                  : ex?.concierge?.agent,
                slaAt: resp?.transferConciergeCase?.concierge?.slaAt ?? ex?.concierge?.slaAt ?? null,
              });
              return { ...row, extra: stringifyExtraIfNeeded(row.extra, ex) };
            }),
          },
        });
      },
      onError: (err) => {
        setActionError(err.message || "Could not transfer case.");
      },
      onCompleted: async (resp) => {
        if (resp?.transferConciergeCase?.success === false) {
          setActionError(resp.transferConciergeCase.message || "Could not transfer case.");
        }
        await refetch();
      },
    },
  );
  const [transferBroadcastGroup, { loading: transferringBroadcastGroup }] = useMutation(
    TRANSFER_CONCIERGE_BROADCAST_GROUP,
    {
      optimisticResponse: ({ targetAgentId }: any) => {
        const target = resolveAgentOptionById(targetAgentId);
        return {
          transferConciergeBroadcastGroup: {
            __typename: "ClaimConciergeCaseResult",
            success: true,
            message: "Screening request transferred.",
            concierge: {
              __typename: "ConciergeState",
              status: "ASSIGNED",
              ownerAgentId: String(targetAgentId || ""),
              ownerAgentName: target?.name || null,
              slaAt: null,
            },
          },
        };
      },
      update: (cache, { data: resp }, { variables }) => {
        if (!variables?.groupId) return;
        const groupId = String(variables.groupId);
        const target = resolveAgentOptionById(variables?.targetAgentId);
        const prev = cache.readQuery<any>({
          query: GET_CONCIERGE_BROADCAST_GROUPS,
          variables: {
            limit: 300,
            offset: 0,
          },
        });
        if (!prev?.getConciergeBroadcastGroups) return;
        cache.writeQuery({
          query: GET_CONCIERGE_BROADCAST_GROUPS,
          variables: {
            limit: 300,
            offset: 0,
          },
          data: {
            ...prev,
            getConciergeBroadcastGroups: prev.getConciergeBroadcastGroups.map((group: any) => {
              if (String(group.id) !== groupId) return group;
              const seed = parseBroadcastSeed(group.seedJson);
              const extra = parseExtra(seed?.extra);
              extra.concierge = applyConciergeAgentState(extra.concierge, {
                status: "ASSIGNED",
                ownerAgentId: String(variables?.targetAgentId || ""),
                ownerAgentName:
                  resp?.transferConciergeBroadcastGroup?.concierge?.ownerAgentName ||
                  target?.name ||
                  null,
                agent: target
                  ? { id: String(target.id), name: target.name, email: target.email || null }
                  : extra?.concierge?.agent,
                slaAt:
                  resp?.transferConciergeBroadcastGroup?.concierge?.slaAt ??
                  extra?.concierge?.slaAt ??
                  null,
              });
              seed.extra = extra;
              return {
                ...group,
                seedJson: stringifySeedIfNeeded(group.seedJson, seed),
              };
            }),
          },
        });
      },
      onError: (err) => {
        setActionError(err.message || "Could not transfer screening request.");
      },
      onCompleted: async (resp) => {
        if (resp?.transferConciergeBroadcastGroup?.success === false) {
          setActionError(
            resp.transferConciergeBroadcastGroup.message ||
            "Could not transfer screening request.",
          );
        }
        await Promise.all([refetch(), refetchScreeningGroups()]);
      },
    },
  );

  const [completeIntake, { loading: completing }] = useMutation(
    COMPLETE_CONCIERGE_INTAKE,
    {
      onCompleted: () => refetch(),
    }
  );

  const [enableAgreement, { loading: enabling }] = useMutation(
    ENABLE_PROVIDER_AGREEMENT,
    {
      onCompleted: () => refetch(),
    }
  );
  const [markReferrerContacted, { loading: markingReferrerContacted }] = useMutation(
    CHANGE_REFERRAL_LEAD_STATUS,
    {
      optimisticResponse: ({ id }: any) => ({
        changeReferralLeadStatus: {
          __typename: "ReferralLead",
          id: String(id),
          status: "QUALIFYING",
        },
      }),
      update: (cache, { data: resp }, { variables }) => {
        if (!variables?.id) return;
        const leadId = String(variables.id);
        const nextStatus =
          String(resp?.changeReferralLeadStatus?.status || "QUALIFYING").toUpperCase();
        const prev = cache.readQuery<any>({
          query: GET_CONCIERGE_LEADS,
          variables: {
            cityCodes: cityFilter?.length ? cityFilter : null,
            statuses: null,
            limit: 300,
            offset: 0,
          },
        });
        if (!prev?.getConciergeLeads) return;

        cache.writeQuery({
          query: GET_CONCIERGE_LEADS,
          variables: {
            cityCodes: cityFilter?.length ? cityFilter : null,
            statuses: null,
            limit: 300,
            offset: 0,
          },
          data: {
            ...prev,
            getConciergeLeads: prev.getConciergeLeads.map((row: any) =>
              String(row.id) === leadId
                ? { ...row, status: nextStatus }
                : row
            ),
          },
        });
      },
      onError: (err) => {
        setActionError(err.message || "Could not mark referrer as contacted.");
      },
      onCompleted: () => { },
    }
  );

  const groups = React.useMemo(() => {
    const byCity: Record<
      string,
      Array<{ key: string; screening?: any; leads: any[]; createdAt?: any }>
    > = {};

    const screeningByGroupId = new Map<string, any>();
    screeningGroups.forEach((group: any) => {
      screeningByGroupId.set(String(group.id), group);
    });

    const bucketByCityAndKey = new Map<string, { key: string; screening?: any; leads: any[]; createdAt?: any }>();
    const ensureBucket = (city: string, key: string) => {
      const compound = `${city}::${key}`;
      let bucket = bucketByCityAndKey.get(compound);
      if (!bucket) {
        bucket = { key, screening: undefined, leads: [], createdAt: null };
        bucketByCityAndKey.set(compound, bucket);
        (byCity[city] = byCity[city] || []).push(bucket);
      }
      return bucket;
    };

    screeningGroups.forEach((group: any) => {
      const city = getBroadcastCityCode(group);
      const key = String(group.id);
      const bucket = ensureBucket(city, key);
      bucket.screening = group;
      bucket.createdAt = group?.createdAt || bucket.createdAt;
    });

    leads.forEach((lead: any) => {
      const groupId = getLeadBroadcastGroupId(lead);
      const linkedScreening = groupId ? screeningByGroupId.get(groupId) : null;
      const city = linkedScreening ? getBroadcastCityCode(linkedScreening) : getCityCode(lead);
      const key = groupId || `lead-${String(lead.id)}`;
      const bucket = ensureBucket(city, key);
      bucket.leads.push(lead);
      bucket.createdAt = bucket.createdAt || lead?.createdAt || null;
    });

    Object.values(byCity).forEach((items) => {
      items.sort((a, b) => {
        const ta = Date.parse(String(a.createdAt || "")) || 0;
        const tb = Date.parse(String(b.createdAt || "")) || 0;
        return tb - ta;
      });
    });

    return byCity;
  }, [leads, screeningGroups]);

  const groupedSections = React.useMemo(() => {
    const mine: typeof groups = {};
    const pool: typeof groups = {};

    const pushBucket = (target: typeof groups, city: string, bucket: any) => {
      if (!target[city]) target[city] = [];
      target[city].push(bucket);
    };

    Object.entries(groups).forEach(([city, buckets]) => {
      buckets.forEach((bucket: any) => {
        const screeningOwnerId = String(
          parseExtra(parseBroadcastSeed(bucket?.screening?.seedJson)?.extra)?.concierge?.ownerAgentId ||
          bucket?.screening?.ownerAgentId ||
          bucket?.screening?.assignedAgentId ||
          "",
        ).trim();
        const leadOwnerIds = (bucket?.leads || [])
          .map((lead: any) =>
            String(
              parseExtra(lead?.extra)?.concierge?.ownerAgentId ||
              lead?.ownerAgentId ||
              lead?.assignedAgentId ||
              ""
            ).trim()
          )
          .filter(Boolean);
        const currentId = String(currentAgentId || "").trim();

        const belongsToMe =
          (screeningOwnerId && screeningOwnerId === currentId) ||
          leadOwnerIds.some((id: string) => id === currentId);
        const isUnclaimed =
          !screeningOwnerId &&
          leadOwnerIds.length === 0;

        if (belongsToMe) pushBucket(mine, city, bucket);
        if (isUnclaimed) pushBucket(pool, city, bucket);
      });
    });

    Object.values(mine).forEach((items) =>
      items.sort((a: any, b: any) => {
        const ta = Date.parse(String(a.createdAt || "")) || 0;
        const tb = Date.parse(String(b.createdAt || "")) || 0;
        return tb - ta;
      }),
    );
    Object.values(pool).forEach((items) =>
      items.sort((a: any, b: any) => {
        const ta = Date.parse(String(a.createdAt || "")) || 0;
        const tb = Date.parse(String(b.createdAt || "")) || 0;
        return tb - ta;
      }),
    );

    return {
      all: groups,
      mine,
      pool,
      allCount: Object.values(groups).reduce((sum, rows) => sum + rows.length, 0),
      mineCount: Object.values(mine).reduce((sum, rows) => sum + rows.length, 0),
      poolCount: Object.values(pool).reduce((sum, rows) => sum + rows.length, 0),
      totalCount: leads.length + screeningGroups.length,
    };
  }, [currentAgentId, groups, leads.length, screeningGroups.length]);

  const activeGroups =
    workspaceTab === 1
      ? groupedSections.mine
      : workspaceTab === 2
        ? groupedSections.pool
        : groupedSections.all;
  const sortedCities = Object.keys(activeGroups).sort((a, b) => a.localeCompare(b));
  const activeRowCount = Object.values(activeGroups).reduce(
    (sum, rows) => sum + rows.length,
    0,
  );
  const activeCityCount = sortedCities.length;

  if (loading || screeningLoading) return <LinearProgress sx={{ my: 2 }} />;

  if (!hasWorkspaceAccess) {
    return (
      <Alert severity="error">
        You do not have access to the agent workspace.
      </Alert>
    );
  }
  if (error || screeningError)
    return (
      <Alert severity="error">
        {String(
          (error as any)?.message ||
          (screeningError as any)?.message ||
          error ||
          screeningError,
        )}
      </Alert>
    );

  return (
    <Box
      sx={{
        maxWidth: 1340,
        mx: "auto",
        px: { xs: 1.25, sm: 2.5, lg: 3 },
        py: { xs: 2, sm: 3.25, lg: 4 },
      }}
    >
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      {actionMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
          {actionMessage}
        </Alert>
      ) : null}
      <Box
        sx={{
          mb: 2.5,
          p: { xs: 2, sm: 2.5, lg: 3 },
          borderRadius: 4,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,255,0.98) 100%)",
          boxShadow: "0 18px 40px rgba(15, 31, 46, 0.08)",
          border: "1px solid rgba(103, 114, 126, 0.12)",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
          spacing={2}
        >
          <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="overline"
              sx={{ letterSpacing: "0.12em", color: "text.secondary", fontWeight: 700 }}
            >
              Care Concierge
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.1 }}>
              Agent Workspace
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              Review screening requests and provider responses in one place with a cleaner queue designed for fast scanning.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ pt: 1, mt: 0.5 }}
            >
              <Chip
                size="small"
                label={`${activeRowCount} requests`}
                sx={{ borderRadius: 999, bgcolor: "rgba(17,47,72,0.06)" }}
              />
              <Chip
                size="small"
                label={`${activeCityCount} areas`}
                sx={{ borderRadius: 999, bgcolor: "rgba(17,47,72,0.06)" }}
              />
            </Stack>
          </Stack>

          <Stack spacing={1.1} sx={{ width: { xs: "100%", lg: 420 }, maxWidth: "100%" }}>
            <WorkspaceShareLinksBox links={workspaceShareLinks} />
            <Button
              size="small"
              variant="outlined"
              href={`${rootClientPath}/care/agent/admin`}
              sx={{ alignSelf: { xs: "stretch", lg: "flex-end" }, borderRadius: 999 }}
            >
              Open Agent Admin
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Tabs
          value={workspaceTab}
          onChange={(_event, next) => setWorkspaceTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 46,
            "& .MuiTabs-flexContainer": {
              gap: 0.75,
            },
            "& .MuiTab-root": {
              minHeight: 42,
              px: 1.6,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              color: "text.secondary",
            },
            "& .Mui-selected": {
              color: "primary.main",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          <Tab label={`All (${groupedSections.allCount})`} />
          <Tab label={`My Assigned (${groupedSections.mineCount})`} />
          <Tab label={`Claim Pool (${groupedSections.poolCount})`} />
        </Tabs>
      </Box>

      {sortedCities.length === 0 ? (
        <Box
          sx={{
            p: 4,
            borderRadius: 4,
            bgcolor: "#ffffff",
            boxShadow: "0 14px 32px rgba(15, 31, 46, 0.06)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            No requests in this view
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Switch tabs or wait for new screening requests and provider responses to appear.
          </Typography>
        </Box>
      ) : null}

      {sortedCities.map((city) => {
        const rows = activeGroups[city];
        const screeningCount = rows.filter((row: any) => Boolean(row?.screening)).length;
        const responseCount = rows.reduce(
          (sum: number, row: any) => sum + (Array.isArray(row?.leads) ? row.leads.length : 0),
          0,
        );
        return (
          <Accordion
            key={city}
            disableGutters
            square={false}
            defaultExpanded={true}
            sx={{
              mb: 2.25,
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 16px 36px rgba(15, 31, 46, 0.06)",
              background: "#ffffff",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.35,
                bgcolor: (t) => alpha(t.palette.info.main, 0.05),
                borderBottom: "1px solid rgba(103, 114, 126, 0.08)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
                    {formatCityGroupLabel(city)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {rows.length} request{rows.length === 1 ? "" : "s"} in this request area
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={`${screeningCount} screening`} />
                  <Chip size="small" label={`${responseCount} responses`} />
                </Stack>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
                <Stack spacing={2}>
                  {rows.map((row) => {
                    const childLeads = [...(row.leads || [])].sort((a, b) => {
                      const ta = Date.parse(String(a?.createdAt || "")) || 0;
                      const tb = Date.parse(String(b?.createdAt || "")) || 0;
                      return tb - ta;
                    });
                    const referenceLabel =
                      getBroadcastReferenceLabel(row.screening) ||
                      getLeadReferenceLabel(childLeads[0]) ||
                      null;
                    const screeningInvites = Array.isArray(row.screening?.invites)
                      ? row.screening.invites
                      : [];
                    const screeningDraftCount = screeningInvites.filter(
                      (invite: any) => String(invite?.status || "").toUpperCase() === "DRAFT",
                    ).length;
                    const screeningSentCount = screeningInvites.filter(
                      (invite: any) => !["", "DRAFT"].includes(String(invite?.status || "").toUpperCase()),
                    ).length;
                    const screeningAcceptedCount = screeningInvites.filter(
                      (invite: any) => String(invite?.status || "").toUpperCase() === "ACCEPTED",
                    ).length;
                    const screeningDeclinedCount = screeningInvites.filter(
                      (invite: any) => String(invite?.status || "").toUpperCase() === "DECLINED",
                    ).length;
                    const acceptedGroupExpanded =
                      expandedAcceptedGroups[row.key] ?? true;
                    const requestGroupExpanded =
                      expandedRequestGroups[row.key] ?? true;

                    return (
                      <Box
                        key={`screening-block-${row.key}`}
                        sx={{
                          p: { xs: 1, sm: 1.25 },
                          borderRadius: 3,
                          bgcolor: "#ffffff",
                          border: "2px solid rgba(69, 85, 101, 0.32)",
                          boxShadow: "0 10px 24px rgba(15, 31, 46, 0.06)",
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: requestGroupExpanded ? 1.25 : 0 }}
                        >
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {row.screening ? "Request Group" : "Provider Response Group"}
                            </Typography>
                            {referenceLabel ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                                Reference: <strong>{referenceLabel}</strong>
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                              <Chip
                                size="small"
                                label={`${childLeads.length}${row.screening ? " responses" : " records"}`}
                                variant="outlined"
                              />
                              {row.screening && screeningDraftCount > 0 ? (
                                <Chip
                                  size="small"
                                  label={`Draft ${screeningDraftCount}`}
                                  variant="outlined"
                                />
                              ) : null}
                              {row.screening ? (
                                <Chip
                                  size="small"
                                  label={`Sent to ${screeningSentCount} providers`}
                                  variant="outlined"
                                />
                              ) : null}
                              {row.screening ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  label={`Accepted ${screeningAcceptedCount}`}
                                  variant="outlined"
                                />
                              ) : null}
                              {row.screening ? (
                                <Chip
                                  size="small"
                                  color="error"
                                  label={`Declined ${screeningDeclinedCount}`}
                                  variant="outlined"
                                />
                              ) : null}
                              <Chip
                                size="small"
                                variant="outlined"
                                label={formatDateWithAgeLabel(row.screening?.createdAt || row.createdAt || null)}
                              />
                            </Stack>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedRequestGroups((prev) => ({
                                  ...prev,
                                  [row.key]: !requestGroupExpanded,
                                }))
                              }
                              sx={{
                                transform: requestGroupExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                transition: "transform 0.2s ease",
                              }}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          </Stack>
                        </Stack>

                        {requestGroupExpanded ? (
                          <>
                            {row.screening ? (
                              <ScreeningRequestRow
                                group={row.screening}
                                rootClientPath={rootClientPath}
                                currentAgentId={currentAgentId}
                                canActAsAgent={providerUserIsAgent}
                                onOpenChat={(v) => setChatReferrer(v)}
                                onClaim={async () => {
                                  try {
                                    setActionError(null);
                                    await claimBroadcastGroup({
                                      variables: { groupId: String(row.screening.id) },
                                    });
                                  } catch (err: any) {
                                    setActionError(
                                      err?.message || "Could not claim screening request.",
                                    );
                                  }
                                }}
                                onUnclaim={async () => {
                                  try {
                                    setActionError(null);
                                    await unclaimBroadcastGroup({
                                      variables: { groupId: String(row.screening.id) },
                                    });
                                  } catch (err: any) {
                                    setActionError(
                                      err?.message || "Could not unclaim screening request.",
                                    );
                                  }
                                }}
                                onTransfer={async (targetAgentId, note) => {
                                  try {
                                    setActionError(null);
                                    await transferBroadcastGroup({
                                      variables: {
                                        groupId: String(row.screening.id),
                                        targetAgentId: String(targetAgentId),
                                        note: note || null,
                                      },
                                    });
                                  } catch (err: any) {
                                    setActionError(
                                      err?.message || "Could not transfer screening request.",
                                    );
                                  }
                                }}
                                claiming={claimingBroadcastGroup}
                                unclaiming={unclaimingBroadcastGroup}
                                transferring={transferringBroadcastGroup}
                                agentOptions={agentOptions}
                              />
                            ) : null}

                            {childLeads.length > 0 ? (
                              <Box
                                sx={{
                                  mt: row.screening ? 1.25 : 0,
                                  pt: row.screening ? 1.25 : 0,
                                  borderTop: row.screening
                                    ? "1px solid rgba(103, 114, 126, 0.10)"
                                    : "none",
                                }}
                              >
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  alignItems={{ xs: "flex-start", sm: "center" }}
                                  justifyContent="space-between"
                                  spacing={1}
                                  sx={{ mb: 1.1, px: 0.25 }}
                                >
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      Provider Responses
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {row.screening
                                        ? "Responses connected to the screening request above."
                                        : "Direct provider response records."}
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Chip size="small" label={childLeads.length} />
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setExpandedAcceptedGroups((prev) => ({
                                          ...prev,
                                          [row.key]: !acceptedGroupExpanded,
                                        }))
                                      }
                                      sx={{
                                        transform: acceptedGroupExpanded
                                          ? "rotate(0deg)"
                                          : "rotate(-90deg)",
                                        transition: "transform 0.2s ease",
                                      }}
                                    >
                                      <ExpandMoreIcon />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                                {acceptedGroupExpanded ? (
                                  <Stack spacing={1.25}>
                                    {childLeads.map((lead) => {
                                      const leadWithHandlers = {
                                        ...lead,
                                        __onClaim: () =>
                                          claimCase({ variables: { leadId: String(lead.id) } }),
                                        __onUnclaim: () =>
                                          unclaimCase({ variables: { leadId: String(lead.id) } }),
                                        __onChangeLeadStatus: (status: string) =>
                                          markReferrerContacted({
                                            variables: { id: String(lead.id), status },
                                          }),
                                        __onMarkReferrerContacted: () =>
                                          markReferrerContacted({
                                            variables: { id: String(lead.id), status: "QUALIFYING" },
                                          }),
                                        __onIntakeDone: () =>
                                          completeIntake({
                                            variables: { leadId: String(lead.id), notes: null },
                                          }),
                                        __onTransfer: (targetAgentId: string, note: string) =>
                                          transferCase({
                                            variables: {
                                              leadId: String(lead.id),
                                              targetAgentId: String(targetAgentId),
                                              note: note || null,
                                            },
                                          }),
                                        __onEnableAgreement: () =>
                                          enableAgreement({
                                            variables: { leadId: String(lead.id) },
                                          }),
                                        __claiming: claiming,
                                        __unclaiming: unclaiming,
                                        __transferring: transferringCase,
                                        __markingContacted: markingReferrerContacted,
                                        __completing: completing,
                                        __enabling: enabling,
                                        __setChatProvider: (v: any) => setChatProvider(v),
                                        __setChatReferrer: (v: any) => setChatReferrer(v),
                                      };
                                      const normalizedLeadStatus = String(
                                        lead?.status || ""
                                      ).toUpperCase();
                                      const isClosedLead = [
                                        "PLACED",
                                        "LOST",
                                        "CANCELLED",
                                        "CLOSED",
                                        "WITHDRAWN",
                                      ].includes(normalizedLeadStatus);
                                      const leadExpanded =
                                        expandedAcceptedLeads[String(lead.id)] ??
                                        !isClosedLead;
                                      const childProviderName =
                                        lead?.business?.name || "Accepted provider";
                                      const childLeadStageLabel =
                                        STAGE_LABELS[normalizedLeadStatus] ||
                                        String(lead?.status || "Unknown");
                                      return (
                                        <Accordion
                                          key={lead.id}
                                          disableGutters
                                          square={false}
                                          expanded={leadExpanded}
                                          onChange={() =>
                                            setExpandedAcceptedLeads((prev) => ({
                                              ...prev,
                                              [String(lead.id)]: !leadExpanded,
                                            }))
                                          }
                                          sx={{
                                            borderRadius: 3,
                                            overflow: "hidden",
                                            bgcolor: "#ffffff",
                                            boxShadow: row.screening
                                              ? "none"
                                              : "0 8px 20px rgba(15, 31, 46, 0.05)",
                                            border: row.screening
                                              ? "1px solid rgba(103, 114, 126, 0.10)"
                                              : "none",
                                            "&:before": { display: "none" },
                                          }}
                                        >
                                          <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                              minHeight: 64,
                                              px: 1.5,
                                              bgcolor: "rgba(249,250,251,0.92)",
                                              "& .MuiAccordionSummary-content": {
                                                my: 1,
                                              },
                                            }}
                                          >
                                            <Stack
                                              direction={{ xs: "column", sm: "row" }}
                                              spacing={1}
                                              alignItems={{ xs: "flex-start", sm: "center" }}
                                              flexWrap="wrap"
                                              sx={{ width: "100%" }}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                              >
                                                {childProviderName}
                                              </Typography>
                                              <Chip
                                                size="small"
                                                label={`Lead: ${childLeadStageLabel}`}
                                                variant="outlined"
                                              />
                                            </Stack>
                                          </AccordionSummary>
                                          <AccordionDetails sx={{ p: 1 }}>
                                            <ConciergeCityBoardLeadRow
                                              l={leadWithHandlers}
                                              currentAgentId={currentAgentId}
                                              canActAsAgent={providerUserIsAgent}
                                              rootClientPath={rootClientPath}
                                              suppressGroupContext={Boolean(row.screening)}
                                              agentOptions={agentOptions}
                                              forceTourReady={Boolean(
                                                tourEnabledByLeadId[String(lead.id)]
                                              )}
                                              onTourEnabled={() =>
                                                setTourEnabledByLeadId((prev) => ({
                                                  ...prev,
                                                  [String(lead.id)]: true,
                                                }))
                                              }
                                            />
                                          </AccordionDetails>
                                        </Accordion>
                                      );
                                    })}
                                  </Stack>
                                ) : null}
                              </Box>
                            ) : null}
                          </>
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Two separate dialogs, one for each side */}
      <ChatDialog
        open={!!chatProvider}
        onClose={() => setChatProvider(null)}
        leadId={chatProvider?.id || null}
        audience="PROVIDER"
        title={
          chatProvider?.name
            ? `Conversation - Provider - ${chatProvider.name}`
            : "Conversation - Provider"
        }
        viewerRole="AGENT"
      />

      <ChatDialog
        open={!!chatReferrer}
        onClose={() => setChatReferrer(null)}
        leadId={chatReferrer?.id || null}
        audience="REFERRER"
        title={
          chatReferrer?.name
            ? `Conversation - Referrer - ${chatReferrer.name}`
            : "Conversation - Referrer"
        }
        viewerRole="AGENT"
      />
    </Box>
  );
}

export const __conciergeCityBoardPageModule = true;
